import {
  /*Tooltip,*/ TreeSelect,
  InputNumber,
  ColorPicker,
  Tooltip,
} from "antd";
import classNames from "classnames";
import React, {
  InputHTMLAttributes,
  PropsWithChildren,
  ReactElement,
  useState,
} from "react";
import { Control, Controller } from "react-hook-form";
import { CheckboxInner } from "./checkbox";
import { DatePickerInner } from "./datePicker";
import { FormField, FormFieldProps, LabelPosition } from "./formField";
import { RadioInner } from "./radio";
import { SelectInner } from "./select";
import { TimePickerInner } from "./timePicker";
import useTranslation from "utils/hooks/useTranslation";

export const InputType = {
  Text: "text",
  Password: "password",
  DatePicker: "datepicker",
  TimePicker: "timePicker",
  Select: "select",
  TreeSelect: "TreeSelect",
  Checkbox: "Checkbox",
  Radio: "Radio",
  TextArea: "textarea",
  Number: "number",
  ColorPicker: "colorPicker",
};
export interface UncontrolledInputProps
  extends Omit<FormFieldProps, "value">,
  InputHTMLAttributes<HTMLInputElement> {
  type?: string;
  label?: string;
  endLabel?: string;
  translateLabel?: boolean;
  icon?: any;
  inputBox?: boolean;
  minimal?: boolean;
  button?: ReactElement;
  rules?: any;
  name?: string;
  control?: never;
  invalid?: any;
  isTouched?: any;
  isDirty?: any;
  error?: any;
  size?: any;
  fixLength?: string;
  labelPosition?: LabelPosition;
  disabledAlpha?: boolean;
  showText?: any;
  translateValue?: boolean;
  minValue?: number;
  maxValue?: number;
  isInteger?: boolean;
  format?: string;
  outFormat?: string;
  useDayJS?: boolean;
  popupClassName?: string;
  allowClear?: boolean;
  defaultPickerValue?: any;
}
export interface ControlledInputProps
  extends Omit<UncontrolledInputProps, "control" | "name"> {
  name: string;
  control: Control<any, any>;
  onChangeCustom?: (value: any) => void;
}
export type InputProps = (ControlledInputProps | UncontrolledInputProps) & {
  confirmEdit?: boolean;
  onChangeCustom?: (value: any, option?: any) => void;
};

const Input: React.FC<InputProps> = React.forwardRef<
  ReactElement,
  PropsWithChildren<InputProps>
>((props, ref) => {
  const { name, control, ...restProps } = props as any;
  return control ? (
    <Controller
      defaultValue={restProps.defaultValue}
      control={control}
      name={name}
      render={({
        field: { onChange, value, ref },
        fieldState: { error, isDirty, invalid, isTouched },
      }) => {
        const handleChange = (val: any) => {
          onChange(val);
          restProps?.onChangeCustom?.(val);
        };

        return (
          <InputInner
            ref={ref}
            isDirty={isDirty}
            invalid={invalid}
            isTouched={isTouched}
            value={value}
            error={error}
            onChange={handleChange}
            name={name}
            {...restProps}
          />
        );
      }}
    />
  ) : (
    <InputInner ref={ref} name={name} {...restProps} />
  );
});

const InputInner: React.FC<InputProps> = React.forwardRef<
  ReactElement,
  PropsWithChildren<InputProps>
>(
  (
    {
      type = InputType.Text,
      className,
      label = "",
      translateLabel = true,
      icon,
      inputBox = false,
      placeholder = " ",
      button,
      invalid,
      isTouched,
      isDirty,
      error,
      //fixLength,
      dataLength,
      labelPosition,
      //confirmEdit,
      borderLess,
      isInteger = true,
      onBlur,
      ...rest
    },
    ref
  ) => {
    //const { t } = useTranslation();
    //const [confirmed, setConfirmed] = useState(false);
    //const { confirm } = useConfirm();

    let input;
    const inputProps = {
      ...rest,
    };
    const size = "compact";
    const antClassName = "form_field_ant";
    let isAnt = false;

    switch (type) {
      case InputType.DatePicker:
        isAnt = true;
        input = (
          <DatePickerInner
            ref={ref}
            placeholder={placeholder}
            size={size}
            //bordered={false}
            variant="borderless"
            className={antClassName}
            min={inputProps.minValue}
            {...(inputProps as any)}
          />
        );
        break;
      case InputType.TimePicker:
        isAnt = true;
        input = (
          <TimePickerInner
            ref={ref}
            //bordered={false}
            variant="borderless"
            placeholder={placeholder}
            size={size}
            showNow={false}
            className={antClassName}
            {...(inputProps as any)}
          />
        );
        break;
      case InputType.Select:
        isAnt = true;
        // if (['multiple', 'tags'].includes((inputProps as SelectProps).mode || '')) {
        //     hasValue = (value as any).length > 0;
        // }
        input = (
          <SelectInner
            {...(inputBox ? { showAction: ["focus"] } : null)}
            ref={ref}
            placeholder={placeholder}
            size={size}
            //bordered={false}
            variant="borderless"
            showAction={"focus"}
            className={antClassName}
            autoComplete={"new-password"}
            {...(inputProps as any)}
            virtual={false}
          />
        );
        break;
      case InputType.TreeSelect:
        isAnt = true;
        input = (
          <TreeSelect
            {...(inputBox ? { showAction: ["focus"] } : null)}
            ref={ref}
            //bordered={false}
            variant="borderless"
            placeholder={placeholder}
            size={size}
            showAction={"focus"}
            className={antClassName}
            {...(inputProps as any)}
          />
        );
        break;
      case InputType.Checkbox:
        input = (
          <CheckboxInner
            ref={ref}
            placeholder={placeholder}
            size={size}
            error={error}
            invalid={invalid}
            isTouched={isTouched}
            icon={icon}
            label={label}
            className={className}
            {...(inputProps as any)}
          />
        );
        break;
      case InputType.Radio:
        input = (
          <RadioInner
            ref={ref}
            placeholder={placeholder}
            size={size}
            error={error}
            invalid={invalid}
            isTouched={isTouched}
            icon={icon}
            className={className}
            label={label}
            {...(inputProps as any)}
          />
        );
        break;
      case InputType.Number:
        isAnt = true;
        input = (
          <InputNumber
            ref={ref}
            variant="borderless"
            placeholder={placeholder}
            {...(inputProps as any)}
            {...(isInteger && {
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                const raw = e.target.value;
                if (!raw) {
                  onBlur?.(e);
                  return;
                }

                const normalized = raw.replace(',', '.');

                const value = Number(normalized);

                if (!isNaN(value)) {
                  const intValue = Math.round(value);
                  inputProps.onChange?.(intValue as any);
                }

                onBlur?.(e);
              },
              onChange: (e) => {
                if (typeof e === 'number') {
                  inputProps.onChange?.(e as any);
                }
              },
              parser: (value) => {
                if (!value) return '';
                const normalized = value.replace(',', '.');

                const n_value = Number(normalized);

                if (!isNaN(n_value)) {
                  return (Math.round(n_value))
                }
              }
            }

            )}



          />
        );
        // input = (
        //   <input
        //     type="number"
        //     ref={ref}
        //     placeholder={placeholder}
        //     {...(inputProps as any)}
        //   />
        // );
        break;
      case InputType.TextArea:
        input = (
          <textarea
            ref={ref}
            placeholder={placeholder}
            {...(inputProps as any)}
          />
        );
        break;
      case InputType.ColorPicker:
        input = (
          <ColorPicker
            arrow={false}
            // ref={ref}
            placeholder={placeholder}
            {...(inputProps as any)}
            onChange={(e) => {
              inputProps.onChange?.(e.toHexString() as any);
            }}
            onMouseDown={(e: any) => {
              e.stopPropagation();
            }}
          />
        );
        break;
      default:
        input = (
          <input
            type={type}
            ref={ref as any}
            placeholder={placeholder}
            {...(type === "password" ? { autoComplete: "new-password" } : null)}
            {...inputProps}
          // onChange={
          //   confirmEdit && !confirmed
          //     ? () => {
          //         confirm({
          //           message: t("#000002050"),
          //           positiveCallback: () => setConfirmed(true),
          //         });
          //       }
          //     : inputProps.onChange
          // }
          />
        );
    }

    const content = ![InputType.Checkbox, InputType.Radio].includes(type) ? (
      <FormField
        className={classNames(className, {
          //   hasValue: (!isTouched && inputProps.defaultValue) || hasValue,
          error: invalid,
          touched: isTouched,
          dirty: isDirty,
        })}
        labelSize={inputProps.labelSize}
        value={inputProps.value}
        label={label}
        translateLabel={translateLabel}
        button={button}
        icon={icon}
        dataLength={dataLength}
        isAnt={isAnt}
        labelPosition={labelPosition}
        error={error?.message}
        borderLess={borderLess}
        size={rest.size}
        info={inputProps.info}
        helpText={inputProps.helpText}
        unit={inputProps.unit}
        emptyClass={inputProps.emptyClass}
        placeholder={placeholder}
        minValue={inputProps.minValue}
        maxValue={inputProps.maxValue}
      // onClick={
      //   rest.disabled && !confirmed && confirmEdit
      //     ? () => {
      //         confirm({
      //           message: t("#000002050"),
      //           positiveCallback: () => setConfirmed(true),
      //         });
      //       }
      //     : undefined
      // }
      >
        {input}
      </FormField>
    ) : (
      input
    );

    return content;
  }
);

export const Password: React.FC<InputProps> = React.forwardRef<
  ReactElement,
  PropsWithChildren<InputProps>
>((props, ref) => {
  const [show, setShow] = useState(false);
  const [blurTimeout, setBlurTimeout] = useState(setTimeout(() => { }));
  const { t } = useTranslation();
  const onBlur = (e: any) => {
    const nativeEvent = e.nativeEvent;
    setBlurTimeout(
      setTimeout(() => {
        if (props.onBlur) {
          return props.onBlur(nativeEvent);
        }
      }, 300)
    );
  };
  return (
    <Input
      ref={ref}
      type={show ? "text" : "password"}
      button={
        <>
          <separator data-vertical="" data-height="input"></separator>
          <Tooltip
            arrow={false}
            title={show ? t("HidePassword") : t("ShowPassword")}
          >
            <div //NOSONAR
              className="button"
              data-height="input"
              data-length="input"
              onClick={() => {
                setShow((sh) => !sh);
                clearTimeout(blurTimeout);
              }}
            >
              <icon>{show ? "lock_open" : "lock"}</icon>
            </div>
          </Tooltip>
        </>
      }
      {...props}
      onBlur={onBlur}
    />
  );
});

export default Input;
